import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth, formatApiError } from '../contexts/AuthContext';
import { LogOut, Download, Trash2, Search, RefreshCw, X, KeyRound, ShieldCheck, ShieldAlert } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_OPTIONS = ['pending', 'contacted', 'confirmed', 'completed', 'cancelled'];

const STATUS_COLORS = {
  pending: { bg: 'rgba(194,168,120,0.12)', border: 'rgba(194,168,120,0.35)', text: '#C2A878' },
  contacted: { bg: 'rgba(110,150,200,0.12)', border: 'rgba(110,150,200,0.35)', text: '#7DA8D6' },
  confirmed: { bg: 'rgba(120,180,140,0.12)', border: 'rgba(120,180,140,0.35)', text: '#88C99F' },
  completed: { bg: 'rgba(140,140,140,0.12)', border: 'rgba(140,140,140,0.35)', text: '#A0A0A0' },
  cancelled: { bg: 'rgba(200,90,90,0.10)', border: 'rgba(200,90,90,0.30)', text: '#D08585' },
};

const TYPE_COLORS = {
  general: { bg: 'rgba(140,140,140,0.10)', text: '#A0A0A0', label: 'General' },
  chauffeur: { bg: 'rgba(194,168,120,0.14)', text: '#C2A878', label: 'Chauffeur' },
};

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-CA', { year: 'numeric', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' });
  } catch {
    return iso;
  }
};

export const AdminDashboard = () => {
  const { user, loading, logout } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [showSessions, setShowSessions] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [selected, setSelected] = useState(null); // booking detail modal
  const [showPwModal, setShowPwModal] = useState(false);

  const fetchData = useCallback(async () => {
    setFetching(true);
    setError('');
    try {
      const [bRes, sRes, sessRes] = await Promise.all([
        axios.get(`${API}/admin/bookings`, { withCredentials: true }),
        axios.get(`${API}/admin/stats`, { withCredentials: true }),
        axios.get(`${API}/admin/sessions`, { withCredentials: true }),
      ]);
      setBookings(bRes.data);
      setStats(sRes.data);
      setSessions(sessRes.data);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setFetching(false);
    }
  }, []);

  useEffect(() => {
    document.title = 'Admin Dashboard | Toronto Royale';
    if (user) fetchData();
  }, [user, fetchData]);

  const filtered = useMemo(() => {
    return bookings.filter((b) => {
      if (statusFilter !== 'all' && b.status !== statusFilter) return false;
      if (typeFilter !== 'all' && (b.type || 'general') !== typeFilter) return false;
      if (search) {
        const q = search.toLowerCase();
        return (
          (b.name || '').toLowerCase().includes(q) ||
          (b.email || '').toLowerCase().includes(q) ||
          (b.phone || '').toLowerCase().includes(q) ||
          (b.booking_id || '').toLowerCase().includes(q) ||
          (b.pickup_location || '').toLowerCase().includes(q) ||
          (b.dropoff_location || '').toLowerCase().includes(q)
        );
      }
      return true;
    });
  }, [bookings, search, statusFilter, typeFilter]);

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#0A0A0A', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p style={{ color: 'rgba(225,225,225,0.8)', fontSize: '13px', letterSpacing: '2px', textTransform: 'uppercase' }}>Loading…</p>
      </div>
    );
  }
  if (!user) return <Navigate to="/admin/login" replace />;

  const handleStatusChange = async (booking_id, newStatus) => {
    try {
      await axios.patch(`${API}/admin/bookings/${booking_id}/status`, { status: newStatus }, { withCredentials: true });
      setBookings((prev) => prev.map((b) => (b.booking_id === booking_id ? { ...b, status: newStatus } : b)));
      fetchData(); // refresh stats
    } catch (err) {
      alert(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const handleDelete = async (booking_id) => {
    if (!window.confirm(`Delete booking ${booking_id}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/bookings/${booking_id}`, { withCredentials: true });
      setBookings((prev) => prev.filter((b) => b.booking_id !== booking_id));
      setSelected(null);
      fetchData();
    } catch (err) {
      alert(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const handleExport = async () => {
    try {
      const res = await axios.get(`${API}/admin/bookings/export.csv`, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `toronto-royale-bookings-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div data-testid="admin-dashboard" style={{ minHeight: '100vh', backgroundColor: '#0A0A0A', color: '#E6E6E6' }}>
      {/* Header */}
      <header style={{ position: 'sticky', top: 0, zIndex: 30, backgroundColor: 'rgba(8,8,8,0.92)', backdropFilter: 'blur(8px)', borderBottom: '1px solid rgba(194,168,120,0.08)', padding: '20px 32px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <p style={{ fontSize: '10px', color: 'rgba(194,168,120,0.6)', letterSpacing: '4px', textTransform: 'uppercase', fontWeight: 300, marginBottom: '4px' }}>Toronto Royale</p>
          <h1 className="font-serif" style={{ fontSize: '20px', fontWeight: 500, color: '#FAFAFA', letterSpacing: '0.3px' }}>Booking Console</h1>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{ fontSize: '12px', color: 'rgba(225,225,225,0.8)' }}>{user.email}</span>
          <button
            data-testid="admin-refresh-btn"
            onClick={fetchData}
            disabled={fetching}
            title="Refresh"
            style={{ background: 'transparent', border: '1px solid rgba(194,168,120,0.18)', color: '#C2A878', padding: '8px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', letterSpacing: '1px', textTransform: 'uppercase' }}
          >
            <RefreshCw size={13} className={fetching ? 'animate-spin' : ''} /> Refresh
          </button>
          <button
            data-testid="admin-export-btn"
            onClick={handleExport}
            style={{ background: 'transparent', border: '1px solid rgba(194,168,120,0.18)', color: '#C2A878', padding: '8px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', letterSpacing: '1px', textTransform: 'uppercase' }}
          >
            <Download size={13} /> CSV
          </button>
          <button
            data-testid="admin-change-password-btn"
            onClick={() => setShowPwModal(true)}
            style={{ background: 'transparent', border: '1px solid rgba(194,168,120,0.18)', color: '#C2A878', padding: '8px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', letterSpacing: '1px', textTransform: 'uppercase' }}
          >
            <KeyRound size={13} /> Password
          </button>
          <button
            data-testid="admin-sessions-btn"
            onClick={() => setShowSessions(true)}
            style={{ background: 'transparent', border: '1px solid rgba(194,168,120,0.18)', color: '#C2A878', padding: '8px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', letterSpacing: '1px', textTransform: 'uppercase' }}
          >
            <ShieldCheck size={13} /> Sessions
          </button>
          <button
            data-testid="admin-logout-btn"
            onClick={logout}
            style={{ background: 'transparent', border: '1px solid rgba(220,90,90,0.25)', color: '#D08585', padding: '8px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', letterSpacing: '1px', textTransform: 'uppercase' }}
          >
            <LogOut size={13} /> Sign Out
          </button>
        </div>
      </header>

      {/* Stats */}
      {stats && (
        <div data-testid="admin-stats" style={{ padding: '32px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px', borderBottom: '1px solid rgba(194,168,120,0.05)' }}>
          <StatCard label="Total Bookings" value={stats.total} accent />
          <StatCard label="Pending" value={stats.by_status.pending} />
          <StatCard label="Confirmed" value={stats.by_status.confirmed} />
          <StatCard label="Completed" value={stats.by_status.completed} />
          <StatCard label="Chauffeur" value={stats.by_type.chauffeur} />
          <StatCard label="General" value={stats.by_type.general} />
        </div>
      )}

      {/* Filters */}
      <div style={{ padding: '24px 32px', display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center', borderBottom: '1px solid rgba(194,168,120,0.05)' }}>
        <div style={{ position: 'relative', flex: '1 1 280px', maxWidth: '420px' }}>
          <Search size={14} style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: 'rgba(225,225,225,0.75)' }} />
          <input
            data-testid="admin-search-input"
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, email, phone, booking ID, location…"
            style={{ width: '100%', backgroundColor: 'rgba(14,14,14,0.6)', border: '1px solid rgba(194,168,120,0.12)', color: '#F5F5F5', padding: '11px 16px 11px 38px', fontSize: '13px', outline: 'none' }}
          />
        </div>
        <select
          data-testid="admin-filter-status"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          style={{ backgroundColor: 'rgba(14,14,14,0.6)', border: '1px solid rgba(194,168,120,0.12)', color: '#F5F5F5', padding: '11px 14px', fontSize: '13px', outline: 'none', minWidth: '140px' }}
        >
          <option value="all">All statuses</option>
          {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
        </select>
        <select
          data-testid="admin-filter-type"
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          style={{ backgroundColor: 'rgba(14,14,14,0.6)', border: '1px solid rgba(194,168,120,0.12)', color: '#F5F5F5', padding: '11px 14px', fontSize: '13px', outline: 'none', minWidth: '140px' }}
        >
          <option value="all">All types</option>
          <option value="general">General</option>
          <option value="chauffeur">Chauffeur</option>
        </select>
        <span style={{ marginLeft: 'auto', fontSize: '12px', color: 'rgba(225,225,225,0.8)' }} data-testid="admin-result-count">
          {filtered.length} of {bookings.length}
        </span>
      </div>

      {/* Table */}
      <div style={{ padding: '24px 32px 80px' }}>
        {error && (
          <div style={{ padding: '14px 18px', marginBottom: '20px', backgroundColor: 'rgba(180,40,40,0.08)', border: '1px solid rgba(220,80,80,0.25)', color: '#E89B9B', fontSize: '13px' }}>
            {error}
          </div>
        )}

        {(() => {
          if (fetching && bookings.length === 0) {
            return <p style={{ textAlign: 'center', padding: '60px', color: 'rgba(225,225,225,0.75)', fontSize: '13px', letterSpacing: '1px', textTransform: 'uppercase' }}>Loading bookings…</p>;
          }
          if (filtered.length === 0) {
            return <p style={{ textAlign: 'center', padding: '60px', color: 'rgba(225,225,225,0.75)', fontSize: '13px' }}>No bookings match your filters.</p>;
          }
          return (
          <div style={{ overflowX: 'auto', backgroundColor: 'rgba(14,14,14,0.4)', border: '1px solid rgba(194,168,120,0.06)' }}>
            <table data-testid="admin-bookings-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ backgroundColor: 'rgba(194,168,120,0.04)', borderBottom: '1px solid rgba(194,168,120,0.1)' }}>
                  <Th>ID</Th>
                  <Th>Type</Th>
                  <Th>Created</Th>
                  <Th>Name</Th>
                  <Th>Contact</Th>
                  <Th>Pickup → Drop-off</Th>
                  <Th>Date/Time</Th>
                  <Th>Status</Th>
                  <Th>Actions</Th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((b) => {
                  const t = TYPE_COLORS[b.type || 'general'];
                  return (
                    <tr
                      key={b.booking_id}
                      data-testid={`booking-row-${b.booking_id}`}
                      style={{ borderBottom: '1px solid rgba(194,168,120,0.04)', cursor: 'pointer' }}
                      onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(194,168,120,0.025)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                      onClick={() => setSelected(b)}
                    >
                      <Td><code style={{ fontSize: '11px', color: '#C2A878' }}>{b.booking_id}</code></Td>
                      <Td>
                        <span style={{ display: 'inline-block', padding: '3px 10px', fontSize: '10px', fontWeight: 500, letterSpacing: '1px', textTransform: 'uppercase', backgroundColor: t.bg, color: t.text, border: `1px solid ${t.text}25` }}>
                          {t.label}
                        </span>
                      </Td>
                      <Td><span style={{ color: 'rgba(225,225,225,0.85)', fontSize: '12px' }}>{formatDate(b.created_at)}</span></Td>
                      <Td><span style={{ color: '#F5F5F5' }}>{b.name || '—'}</span></Td>
                      <Td>
                        <div style={{ fontSize: '12px', color: 'rgba(204,204,204,0.7)' }}>{b.email || '—'}</div>
                        <div style={{ fontSize: '11px', color: 'rgba(204,204,204,0.45)' }}>{b.phone || '—'}</div>
                      </Td>
                      <Td>
                        <div style={{ fontSize: '12px', color: 'rgba(204,204,204,0.65)', maxWidth: '260px' }}>
                          <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{b.pickup_location || '—'}</div>
                          <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: 'rgba(225,225,225,0.75)' }}>↓ {b.dropoff_location || '—'}</div>
                        </div>
                      </Td>
                      <Td><span style={{ fontSize: '12px', color: 'rgba(225,225,225,0.85)' }}>{b.date_time || '—'}</span></Td>
                      <Td onClick={(e) => e.stopPropagation()}>
                        <select
                          data-testid={`status-select-${b.booking_id}`}
                          value={b.status || 'pending'}
                          onChange={(e) => handleStatusChange(b.booking_id, e.target.value)}
                          style={{
                            backgroundColor: STATUS_COLORS[b.status]?.bg || STATUS_COLORS.pending.bg,
                            border: `1px solid ${STATUS_COLORS[b.status]?.border || STATUS_COLORS.pending.border}`,
                            color: STATUS_COLORS[b.status]?.text || STATUS_COLORS.pending.text,
                            padding: '5px 8px', fontSize: '11px', fontWeight: 500, letterSpacing: '0.5px', textTransform: 'uppercase', cursor: 'pointer', outline: 'none',
                          }}
                        >
                          {STATUS_OPTIONS.map((s) => <option key={s} value={s} style={{ backgroundColor: '#0A0A0A', color: '#F5F5F5' }}>{s}</option>)}
                        </select>
                      </Td>
                      <Td onClick={(e) => e.stopPropagation()}>
                        <button
                          data-testid={`delete-btn-${b.booking_id}`}
                          onClick={() => handleDelete(b.booking_id)}
                          title="Delete"
                          style={{ background: 'transparent', border: 'none', color: 'rgba(220,90,90,0.6)', cursor: 'pointer', padding: '4px' }}
                          onMouseEnter={(e) => { e.currentTarget.style.color = '#D08585'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(220,90,90,0.6)'; }}
                        >
                          <Trash2 size={14} />
                        </button>
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          );
        })()}
      </div>

      {/* Detail Modal */}
      {selected && (
        <div
          data-testid="booking-detail-modal"
          onClick={() => setSelected(null)}
          style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(6px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px', zIndex: 50 }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ width: '100%', maxWidth: '640px', maxHeight: '90vh', overflowY: 'auto', backgroundColor: '#0E0E0E', border: '1px solid rgba(194,168,120,0.15)', padding: '36px 40px', position: 'relative' }}
          >
            <button
              data-testid="modal-close-btn"
              onClick={() => setSelected(null)}
              style={{ position: 'absolute', top: '20px', right: '20px', background: 'transparent', border: 'none', color: 'rgba(225,225,225,0.8)', cursor: 'pointer' }}
            >
              <X size={18} />
            </button>
            <p style={{ fontSize: '10px', color: 'rgba(194,168,120,0.6)', letterSpacing: '3px', textTransform: 'uppercase', marginBottom: '8px' }}>Booking Detail</p>
            <h2 className="font-serif" style={{ fontSize: '24px', fontWeight: 500, color: '#FAFAFA', marginBottom: '4px' }}>#{selected.booking_id}</h2>
            <p style={{ fontSize: '12px', color: 'rgba(225,225,225,0.8)', marginBottom: '28px' }}>
              {TYPE_COLORS[selected.type || 'general'].label} · {formatDate(selected.created_at)}
            </p>

            <DetailRow label="Name" value={selected.name} />
            <DetailRow label="Email" value={selected.email} link={selected.email ? `mailto:${selected.email}` : null} />
            <DetailRow label="Phone" value={selected.phone} link={selected.phone ? `tel:${selected.phone}` : null} />
            <DetailRow label="Pickup" value={selected.pickup_location} />
            <DetailRow label="Drop-off" value={selected.dropoff_location} />
            <DetailRow label="Date/Time" value={selected.date_time} />
            {selected.passengers && <DetailRow label="Passengers" value={selected.passengers} />}
            {selected.vehicle_preference && <DetailRow label="Vehicle" value={selected.vehicle_preference} />}
            {selected.occasion && <DetailRow label="Occasion" value={selected.occasion} />}
            {selected.notes && <DetailRow label="Notes" value={selected.notes} />}
            <DetailRow label="Status" value={selected.status} />
          </div>
        </div>
      )}

      {showPwModal && <ChangePasswordModal onClose={() => setShowPwModal(false)} />}
      {showSessions && <SessionsModal sessions={sessions} onClose={() => setShowSessions(false)} />}
    </div>
  );
};

const SessionsModal = ({ sessions, onClose }) => {
  const successCount = sessions.filter((s) => s.success).length;
  const failedCount = sessions.length - successCount;
  return (
    <div
      data-testid="sessions-modal"
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(6px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px', zIndex: 60 }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ width: '100%', maxWidth: '760px', maxHeight: '85vh', display: 'flex', flexDirection: 'column', backgroundColor: '#0E0E0E', border: '1px solid rgba(194,168,120,0.15)', position: 'relative' }}
      >
        <div style={{ padding: '32px 40px 20px', borderBottom: '1px solid rgba(194,168,120,0.08)' }}>
          <button
            type="button"
            onClick={onClose}
            data-testid="sessions-close-btn"
            style={{ position: 'absolute', top: '20px', right: '20px', background: 'transparent', border: 'none', color: 'rgba(225,225,225,0.8)', cursor: 'pointer' }}
          >
            <X size={18} />
          </button>
          <p style={{ fontSize: '10px', color: 'rgba(194,168,120,0.6)', letterSpacing: '3px', textTransform: 'uppercase', marginBottom: '8px' }}>Security</p>
          <h2 className="font-serif" style={{ fontSize: '22px', fontWeight: 500, color: '#FAFAFA', marginBottom: '12px' }}>Recent Login Activity</h2>
          <p style={{ fontSize: '12px', color: 'rgba(225,225,225,0.8)' }}>
            Last {sessions.length} login attempts ·{' '}
            <span style={{ color: '#88C99F' }}>{successCount} successful</span> ·{' '}
            <span style={{ color: '#D08585' }}>{failedCount} failed</span>
          </p>
          {failedCount > 0 && (
            <p style={{ fontSize: '12px', color: '#D08585', marginTop: '10px', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <ShieldAlert size={13} /> If you don't recognize a failed login, consider rotating your password.
            </p>
          )}
        </div>

        <div style={{ overflowY: 'auto', flex: 1 }}>
          {sessions.length === 0 ? (
            <p style={{ padding: '40px', textAlign: 'center', color: 'rgba(225,225,225,0.75)', fontSize: '13px' }}>
              No login activity yet.
            </p>
          ) : (
            <table data-testid="sessions-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
              <thead style={{ position: 'sticky', top: 0, backgroundColor: '#0E0E0E' }}>
                <tr style={{ borderBottom: '1px solid rgba(194,168,120,0.08)' }}>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '10px', color: 'rgba(194,168,120,0.7)', letterSpacing: '2px', textTransform: 'uppercase', fontWeight: 500 }}>Status</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '10px', color: 'rgba(194,168,120,0.7)', letterSpacing: '2px', textTransform: 'uppercase', fontWeight: 500 }}>Time</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '10px', color: 'rgba(194,168,120,0.7)', letterSpacing: '2px', textTransform: 'uppercase', fontWeight: 500 }}>IP</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '10px', color: 'rgba(194,168,120,0.7)', letterSpacing: '2px', textTransform: 'uppercase', fontWeight: 500 }}>Device</th>
                </tr>
              </thead>
              <tbody>
                {sessions.map((s, i) => (
                  <tr key={i} data-testid={`session-row-${i}`} style={{ borderBottom: '1px solid rgba(194,168,120,0.04)' }}>
                    <td style={{ padding: '12px 16px' }}>
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: '5px',
                        padding: '3px 9px', fontSize: '10px', fontWeight: 500, letterSpacing: '1px', textTransform: 'uppercase',
                        backgroundColor: s.success ? 'rgba(120,180,140,0.10)' : 'rgba(200,90,90,0.08)',
                        color: s.success ? '#88C99F' : '#D08585',
                        border: `1px solid ${s.success ? 'rgba(120,180,140,0.3)' : 'rgba(200,90,90,0.3)'}`,
                      }}>
                        {s.success ? <ShieldCheck size={11} /> : <ShieldAlert size={11} />}
                        {s.success ? 'Success' : 'Failed'}
                      </span>
                    </td>
                    <td style={{ padding: '12px 16px', color: 'rgba(225,225,225,0.85)' }}>{formatRelativeTime(s.timestamp)}</td>
                    <td style={{ padding: '12px 16px', color: 'rgba(204,204,204,0.55)' }}><code style={{ fontSize: '11px' }}>{s.ip || '—'}</code></td>
                    <td style={{ padding: '12px 16px', color: 'rgba(204,204,204,0.45)', maxWidth: '280px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {parseUserAgent(s.user_agent)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

const formatRelativeTime = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    const diffMs = Date.now() - d.getTime();
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins} min ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return d.toLocaleString('en-CA', { month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' });
  } catch {
    return iso;
  }
};

const parseUserAgent = (ua) => {
  if (!ua) return '—';
  return `${detectBrowser(ua)}${detectOS(ua) ? ` · ${detectOS(ua)}` : ''}`;
};

const detectBrowser = (ua) => {
  if (/Edg\//.test(ua)) return 'Edge';
  if (/Chrome\//.test(ua) && !/Chromium\//.test(ua)) return 'Chrome';
  if (/Firefox\//.test(ua)) return 'Firefox';
  if (/Safari\//.test(ua) && !/Chrome\//.test(ua)) return 'Safari';
  if (/Opera|OPR\//.test(ua)) return 'Opera';
  if (/HeadlessChrome|playwright|curl/i.test(ua)) return 'Bot/CLI';
  return 'Browser';
};

const detectOS = (ua) => {
  if (/Windows NT/.test(ua)) return 'Windows';
  if (/Mac OS X/.test(ua)) return 'macOS';
  if (/iPhone|iPad/.test(ua)) return 'iOS';
  if (/Android/.test(ua)) return 'Android';
  if (/Linux/.test(ua)) return 'Linux';
  return '';
};

const ChangePasswordModal = ({ onClose }) => {
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (newPw.length < 8) {
      setError('New password must be at least 8 characters.');
      return;
    }
    if (newPw !== confirmPw) {
      setError('New password and confirmation do not match.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/auth/change-password`,
        { current_password: currentPw, new_password: newPw },
        { withCredentials: true }
      );
      setSuccess(true);
      setTimeout(onClose, 1600);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="change-password-modal"
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(6px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px', zIndex: 60 }}
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={handleSubmit}
        style={{ width: '100%', maxWidth: '440px', backgroundColor: '#0E0E0E', border: '1px solid rgba(194,168,120,0.15)', padding: '40px 44px', position: 'relative' }}
      >
        <button
          type="button"
          onClick={onClose}
          data-testid="change-password-close-btn"
          style={{ position: 'absolute', top: '20px', right: '20px', background: 'transparent', border: 'none', color: 'rgba(225,225,225,0.8)', cursor: 'pointer' }}
        >
          <X size={18} />
        </button>
        <p style={{ fontSize: '10px', color: 'rgba(194,168,120,0.6)', letterSpacing: '3px', textTransform: 'uppercase', marginBottom: '8px' }}>Security</p>
        <h2 className="font-serif" style={{ fontSize: '22px', fontWeight: 500, color: '#FAFAFA', marginBottom: '28px' }}>Change Password</h2>

        {success ? (
          <div data-testid="change-password-success" style={{ padding: '16px 18px', backgroundColor: 'rgba(120,180,140,0.1)', border: '1px solid rgba(120,180,140,0.3)', color: '#88C99F', fontSize: '13px' }}>
            Password updated successfully.
          </div>
        ) : (
          <>
            <PwField testId="current-password" label="Current Password" value={currentPw} onChange={setCurrentPw} />
            <PwField testId="new-password" label="New Password (min 8 chars)" value={newPw} onChange={setNewPw} />
            <PwField testId="confirm-password" label="Confirm New Password" value={confirmPw} onChange={setConfirmPw} />

            {error && (
              <div data-testid="change-password-error" style={{ marginBottom: '16px', padding: '11px 13px', backgroundColor: 'rgba(180,40,40,0.08)', border: '1px solid rgba(220,80,80,0.25)', color: '#E89B9B', fontSize: '12px' }}>
                {error}
              </div>
            )}

            <button
              type="submit"
              data-testid="change-password-submit"
              disabled={submitting}
              style={{ width: '100%', padding: '13px', backgroundColor: '#C2A878', color: '#0A0A0A', fontSize: '12px', fontWeight: 600, letterSpacing: '2px', textTransform: 'uppercase', border: 'none', cursor: submitting ? 'wait' : 'pointer', opacity: submitting ? 0.6 : 1 }}
            >
              {submitting ? 'Updating…' : 'Update Password'}
            </button>
          </>
        )}
      </form>
    </div>
  );
};

const PwField = ({ testId, label, value, onChange }) => (
  <div style={{ marginBottom: '20px' }}>
    <label style={{ display: 'block', fontSize: '10px', color: 'rgba(194,168,120,0.7)', letterSpacing: '2px', textTransform: 'uppercase', marginBottom: '8px', fontWeight: 400 }}>{label}</label>
    <input
      data-testid={testId}
      type="password"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      required
      style={{ width: '100%', backgroundColor: 'rgba(8,8,8,0.7)', border: '1px solid rgba(194,168,120,0.15)', color: '#F5F5F5', padding: '11px 14px', fontSize: '13px', outline: 'none' }}
    />
  </div>
);

const StatCard = ({ label, value, accent }) => (
  <div style={{ backgroundColor: 'rgba(14,14,14,0.5)', border: '1px solid rgba(194,168,120,0.08)', padding: '20px 22px', position: 'relative', overflow: 'hidden' }}>
    {accent && <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '1px', background: 'linear-gradient(90deg, transparent, rgba(194,168,120,0.5), transparent)' }} />}
    <p style={{ fontSize: '10px', color: 'rgba(194,168,120,0.55)', letterSpacing: '2px', textTransform: 'uppercase', marginBottom: '10px', fontWeight: 400 }}>{label}</p>
    <p className="font-serif" style={{ fontSize: '28px', fontWeight: 500, color: accent ? '#C2A878' : '#FAFAFA' }}>{value}</p>
  </div>
);

const Th = ({ children }) => (
  <th style={{ padding: '14px 16px', textAlign: 'left', fontSize: '10px', fontWeight: 500, color: 'rgba(194,168,120,0.7)', letterSpacing: '2px', textTransform: 'uppercase' }}>{children}</th>
);

const Td = ({ children, ...props }) => (
  <td {...props} style={{ padding: '14px 16px', verticalAlign: 'middle' }}>{children}</td>
);

const DetailRow = ({ label, value, link }) => (
  <div style={{ display: 'flex', gap: '16px', padding: '12px 0', borderBottom: '1px solid rgba(194,168,120,0.05)' }}>
    <span style={{ flex: '0 0 110px', fontSize: '11px', color: 'rgba(194,168,120,0.6)', letterSpacing: '1.5px', textTransform: 'uppercase', paddingTop: '2px' }}>{label}</span>
    <span style={{ flex: 1, fontSize: '14px', color: '#F0F0F0', wordBreak: 'break-word' }}>
      {link ? <a href={link} style={{ color: '#C2A878', textDecoration: 'none' }}>{value}</a> : (value || '—')}
    </span>
  </div>
);
